import { Link } from 'react-router';

import routeResultsCapture from '../../../../screenshots/stage_2/captures/route-results-1440x900.png';

import { ContactExperience } from './ContactForm';

const PRODUCT_STEPS = [
  {
    description: 'Import KML or KMZ.',
    number: '01',
    title: 'Add the route',
  },
  {
    description: 'Choose reviewed pump, curve, hose, and driver data.',
    number: '02',
    title: 'Select equipment',
  },
  {
    description: 'Enter flow, pressure, and safety limits.',
    number: '03',
    title: 'Set requirements',
  },
  {
    description: 'Check pump placement and export the result.',
    number: '04',
    title: 'Review the result',
  },
] as const;

interface ProductCaptureProps {
  alt: string;
  caption: string;
  hero?: boolean;
  src: string;
}

function ProductCapture({ alt, caption, hero = false, src }: ProductCaptureProps) {
  return (
    <figure
      className={`public-illustration${hero ? ' public-illustration--hero' : ''}`}
    >
      <img
        alt={alt}
        className="public-capture-image"
        decoding="async"
        height="900"
        loading={hero ? 'eager' : 'lazy'}
        src={src}
        width="1440"
      />
      <figcaption>
        <strong>Product preview</strong>
        <span>{caption}</span>
      </figcaption>
    </figure>
  );
}

export function PublicLandingPage() {
  return (
    <article className="public-landing">
      <section className="public-panel public-hero" aria-labelledby="public-title">
        <div className="public-panel__inner public-hero__grid">
          <div className="public-hero__copy">
            <p className="eyebrow">Water-transfer planning</p>
            <h1 id="public-title" tabIndex={-1}>
              Plan water transfers with fewer pumps
            </h1>
            <p className="public-lede">
              Import a permitted route, select reviewed equipment, and calculate
              pump placement.
            </p>
            <div className="button-row public-hero__actions">
              <Link className="button button--primary" to="/app">
                Start a project
              </Link>
            </div>
            <div className="public-boundary" role="note">
              <strong>Planning aid.</strong>
              <span>Qualified review is required before field use.</span>
            </div>
          </div>
          <ProductCapture
            alt="Flow Wizard route results screen"
            caption="Example data. Not an approved result."
            hero
            src={routeResultsCapture}
          />
        </div>
      </section>

      <section
        className="public-panel public-workflow"
        id="how-it-works"
        aria-labelledby="public-workflow-heading"
        tabIndex={-1}
      >
        <div className="public-panel__inner">
          <header className="public-section-heading public-section-heading--simple">
            <h2 id="public-workflow-heading">How it works</h2>
          </header>
          <ol className="public-steps">
            {PRODUCT_STEPS.map(({ description, number, title }) => (
              <li key={number}>
                <span>{number}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        className="public-panel public-capability-contact"
        id="contact"
        aria-labelledby="public-contact-heading"
        tabIndex={-1}
      >
        <div className="public-panel__inner">
          <div className="public-contact public-contact--standalone">
            <div className="public-contact__intro">
              <h2 id="public-contact-heading">Contact Flow Wizard</h2>
              <p>
                Do not send credentials, permit identifiers, private coordinates,
                or sensitive records.
              </p>
            </div>
            <ContactExperience />
          </div>
        </div>
      </section>
    </article>
  );
}

function LegalIntroduction({
  description,
  eyebrow,
  title,
  version = 'Stage 2 notice · 27 August 2026',
}: {
  description: string;
  eyebrow: string;
  title: string;
  version?: string;
}) {
  return (
    <header className="public-legal__header">
      <p className="eyebrow">{eyebrow}</p>
      <h1 tabIndex={-1}>{title}</h1>
      <p>{description}</p>
      <p className="public-legal__version">{version}</p>
    </header>
  );
}

export function PrivacyPage() {
  return (
    <article className="public-legal">
      <LegalIntroduction
        description="What the public site, contact service, authenticated workspace, and configured providers process—and how long Flow Wizard keeps its local records."
        eyebrow="Public information"
        title="Privacy"
        version="Updated 7 September 2026"
      />
      <div className="public-legal__body">
        <section
          aria-labelledby="pump-curve-analysis-heading"
          id="pump-curve-analysis"
          tabIndex={-1}
        >
          <h2 id="pump-curve-analysis-heading">Pump documents and curve analysis</h2>
          <p className="public-legal__version">
            Curve-analysis policy: curve-analysis-privacy/3.0
          </p>
          <p>
            When you choose Analyze, you authorize Flow Wizard and its service
            providers, including OpenAI, to analyze your uploaded pump document.
            We send images and extracted text from the selected pages, plus the
            selected graph crop—not the original PDF or unselected pages.
            Uploading alone does not send your document to OpenAI.
          </p>
          <p>
            Flow Wizard retains source documents, original and modified curve
            records, and analysis and review history for engineering traceability
            and private reviewed lookup. These engineering records have no
            automatic expiry. Authorized human reviewers may inspect them.
            Approved curves may be reused when a user uploads a matching pump
            document; the library is not publicly searchable.
            Modified copies remain separate and do not inherit library approval.
          </p>
          <p>
            OpenAI does not use API content for model training by default.
            Provider retention and exceptions follow{' '}
            <a href="https://developers.openai.com/api/docs/guides/your-data">
              OpenAI’s data controls
            </a>
            . Flow Wizard disables stored response retrieval, but this does not
            guarantee zero provider retention.
          </p>
          <p>
            Only upload documents you have permission to use and share for this
            purpose. Check extracted curves, units, and limits; qualified
            engineering review is required before field use.
          </p>
        </section>

        <section>
          <h2>Public and authenticated boundaries</h2>
          <p>
            You can read the public landing, privacy, and terms pages without an
            account. Those pages do not ask the authenticated APIs for account,
            project, route, equipment, or calculation data. The server and its
            trusted proxy may retain allowlisted security and diagnostic events
            for up to 30 days; these records exclude contact content,
            credentials, challenge tokens, and raw provider responses.
          </p>
          <p>
            The authenticated application processes the account and project
            data needed to save routes, reviewed equipment, scenarios,
            calculations, results, and exports. Public contact fields,
            idempotency keys, and challenge tokens are held only in the current
            page's memory by the client and are not written to Flow Wizard
            browser storage or analytics.
          </p>
        </section>

        <section>
          <h2>Contact inquiries</h2>
          <p>
            When the contact service is ready and you consent to the displayed
            privacy-notice version, Flow Wizard receives your name, email,
            optional organization, message, a single-use challenge token, and a
            request identifier. It uses these records to prevent duplicate
            submissions and abuse, deliver the inquiry to the owner, and send a
            limited receipt. The receipt does not repeat your submitted message.
          </p>
          <p>
            Locally, an accepted inquiry is retained for 90 days. Active
            idempotency reservations and outcomes are capped at 48 hours and
            2,000 records. After successful delivery, delivery metadata is kept
            for 30 days and the body is scrubbed; a retry body is retained for
            up to 7 days and a dead-letter body for up to 30 days. Throttle
            identifiers are retained for 48 hours, aggregate non-personal
            metrics for 180 days, and contact backups for 30 days.
          </p>
        </section>

        <section>
          <h2>Cloudflare Turnstile</h2>
          <p>
            When the form is enabled, Cloudflare Turnstile supplies the required
            human-verification challenge. The browser sends Cloudflare the site
            key and challenge-related device, network, and interaction signals;
            Flow Wizard sends the resulting token to Cloudflare for server-side
            verification with the fixed <code>contact_submit</code> action. The
            token is single-use, expires after five minutes, and is not retained
            as application content after the attempt.
          </p>
          <p>
            Cloudflare controls its own provider records and retention under its{' '}
            <a href="https://www.cloudflare.com/privacypolicy/">
              privacy policy
            </a>
            . Removing a local Flow Wizard record does not remove an independent
            provider record.
          </p>
        </section>

        <section>
          <h2>Brevo email delivery</h2>
          <p>
            When configured, Brevo processes the contact name, reply address,
            organization, and message to deliver the owner notification. It may
            also process the name and address for the fixed visitor receipt.
            Production release requires tracking anonymization and the approved
            sender and recipient configuration. Brevo controls its own delivery
            records and retention under its service terms; local deletion does
            not erase provider-held records.
          </p>
          <p>
            SMTP delivery is at least once. A provider may accept a message even
            when Flow Wizard cannot confirm that acceptance, so a retry can
            produce a duplicate owner notification or receipt.
          </p>
        </section>

        <section>
          <h2>Maps and terrain providers</h2>
          <p>
            Google Maps JavaScript loads only in the authenticated map workspace
            when it is configured. Google may receive network information,
            browser referrer, and map interaction or requested-location data
            under its provider terms. Flow Wizard does not use or persist Google
            Elevation data for hydraulic terrain, and the public illustrations
            do not contain rehosted Google map content.
          </p>
          <p>
            When enabled for licensed global terrain, the commercial Open-Meteo
            endpoint supplies Copernicus DEM GLO-90 data. Flow Wizard retains
            the selected terrain result and provenance with the project and its
            exports. Copernicus GLO-90 is a roughly 90-metre digital surface
            model, not surveyed ground, is referenced to EGM2008, and is
            licensed under CC BY 4.0 with required attribution. Provider request
            and account records remain subject to the provider's terms.
          </p>
        </section>

        <section>
          <h2>Choices and questions</h2>
          <p>
            Do not use the public form for credentials, private coordinates,
            permit identifiers, or regulated records. If the form is enabled,
            you may use it to ask about a contact record; include the public
            reference rather than repeating sensitive content. If it is not
            enabled, Flow Wizard queues no inquiry or email.
          </p>
          <p>
            Presentation preferences used by the authenticated application may
            remain in browser storage. They do not contain contact content,
            email, request identifiers, or challenge tokens.
          </p>
        </section>
      </div>
    </article>
  );
}

export function TermsPage() {
  return (
    <article className="public-legal">
      <LegalIntroduction
        description="The operating boundary for Flow Wizard planning, provider-backed data, contact delivery, and engineering review."
        eyebrow="Public information"
        title="Terms"
      />
      <div className="public-legal__body">
        <section>
          <h2>Design assist, not approval</h2>
          <p>
            Flow Wizard supports water-transfer planning and optimization. It
            does not issue permits, certify equipment, supervise field work, or
            replace qualified engineering judgment. Results are modeled outputs,
            not guaranteed field performance or engineering recommendations.
          </p>
          <p>
            Before construction or operation, a qualified reviewer must compare
            the route, elevations, equipment, assumptions, warnings, margins,
            proof status, and outputs with field conditions, applicable laws,
            standards, permits, manufacturer data, and operating procedures.
          </p>
        </section>

        <section>
          <h2>Routes and input responsibility</h2>
          <p>
            Users must supply routes they are permitted to use and have the
            rights needed to upload each file. Users are responsible for
            checking imported feature roles, coordinates, topology, terrain,
            candidate sites, equipment curves and limits, safety settings,
            scenarios, and unit interpretation. A successful import or solve
            does not validate a permit or the underlying source material.
          </p>
        </section>

        <section>
          <h2>Optimization, evidence, and exports</h2>
          <p>
            Minimum-pump placement is constrained by the reviewed model and the
            approved candidate set; it does not search or approve unrestricted
            field locations. Warnings, assumptions, provenance, calculation
            settings, and proof limitations are part of the result. Preserve
            those records when sharing or relying on an export.
          </p>
        </section>

        <section>
          <h2>Provider-backed maps and terrain</h2>
          <p>
            When configured, Google Maps is an authenticated browser basemap and
            interaction provider subject to Google's terms. Google map content
            may not be extracted or rehosted through Flow Wizard, and Google
            Elevation is not a persisted hydraulic terrain source.
          </p>
          <p>
            When enabled, licensed global terrain comes from the commercial
            Open-Meteo endpoint using Copernicus DEM GLO-90. Copernicus data is
            licensed under CC BY 4.0 and requires attribution. GLO-90 is an
            EGM2008-referenced, roughly 90-metre digital surface model rather
            than survey-grade ground elevation. Provider availability and terms
            can change; stored provenance and warnings remain part of review.
          </p>
        </section>

        <section>
          <h2>Contact and email delivery</h2>
          <p>
            The public form is available only when its privacy, Cloudflare
            Turnstile, rate-limit, circuit-breaker, recipient, and Brevo delivery
            gates are ready. A public acceptance reference or automatic receipt
            confirms receipt only; it does not promise a response time, create
            an engineering engagement, or approve submitted information.
          </p>
          <p>
            Email delivery is at least once, so ambiguous provider acceptance
            followed by retry can create a duplicate. Do not send credentials,
            private coordinates, permit identifiers, or other sensitive or
            regulated records through the public form.
          </p>
        </section>

        <section>
          <h2>Access and availability</h2>
          <p>
            The workspace is limited to accounts authorized by its
            administrator. Users must protect their account access and may not
            bypass access controls, abuse provider services, or interfere with
            the contact safeguards. Features and providers can be unavailable;
            retain the review evidence needed for your own records.
          </p>
        </section>
      </div>
    </article>
  );
}
